import { VersionType } from "./constants";
import { booksOfTheBible, matchesBookName } from "./GenerateSelectorOptions";

interface ParsedResult{
    book: string;
    chapter: number;
    verseStart?: number;
    verseEnd?: number;
}



const capitalizeFirstIfEnglish = (input: string): string => {
    if (input.length === 0) {
        return input;
    }

    const firstChar = input.charAt(0);

    // Check if the first character is an English letter using a regex
    if (/^[a-zA-Z]$/.test(firstChar)) {
        return firstChar.toUpperCase() + input.slice(1);
    }

    return input;
}

const convertToFullBookName = (book: string, versions:VersionType[]): string => {
    const isTraditional = versions.includes(VersionType.ZHTW);
    for (let i = 0; i < booksOfTheBible.length; i++) {
        const option = booksOfTheBible[i];

        if (option.simAbbrev === book || option.triAbbrev === book) {
            return isTraditional ? option.traditional : option.simplified;

        } else if (option.engAbbrev === book) {
            return option.english;
        }
    }
    return book;
};




export const reconstructQueryString = (query:string, book:string, chapter:number, verseStart:number | undefined, verseEnd:number | undefined, versions:VersionType[]): string => {
    const baseString = `${convertToFullBookName(book,versions)} ` + (chapter ? `${chapter}` : '');

    if (query.includes(":")) {
        if (verseStart && verseEnd) {
            return (`${baseString}:${verseStart}-${verseEnd}`);
        } else if (verseStart) {
            return (`${baseString}:${verseStart}`);
        } else {
            return baseString;
        }
    } else {
        if (verseStart && verseEnd) {
            return (`${baseString} ${verseStart} ${verseEnd}`);
        } else if (verseStart) {
            return (`${baseString} ${verseStart}`);
        } else {
            return baseString
        }
    }}

    

export const parseString = (inputStr: string): ParsedResult => {
    let match = inputStr.match(chinesePattern);
    
    if (!match) {
        match = inputStr.match(englishPattern);
    }

    if (match && match.groups) {
        const book = capitalizeFirstIfEnglish(match.groups.book);
        const chapter = parseInt(match.groups.chapter);
        let verseStart: number | undefined;
        let verseEnd: number | undefined;

        if (match.groups.verseStart) {
            verseStart = parseInt(match.groups.verseStart);
        }
        if (match.groups.verseEnd) {
            verseEnd = parseInt(match.groups.verseEnd);
        }
        if (match.groups.verseStartAlt) {
            verseStart = parseInt(match.groups.verseStartAlt);
        }
        if (match.groups.verseEndAlt) {
            verseEnd = parseInt(match.groups.verseEndAlt);
        }
        return { book, chapter, verseStart, verseEnd };
    } else {
        //  No match
        return { book: '', chapter: 0}
    }
}

const chinesePattern = /(?<book>[\u4e00-\u9fa5]+)(?:\s)?(?<chapter>\d+)(?:\s(?<verseStart>\d+)(?:\s(?<verseEnd>\d+))?)?(?::(?<verseStartAlt>\d+)(?:-(?<verseEndAlt>\d+))?)?/;
const englishPattern = /(?<book>(?:\d_)?[a-zA-Z]+(?:_[a-zA-Z]+)*)(?:\s)?(?<chapter>\d+)(?:[:\s](?<verseStart>\d+)(?:[-\s](?<verseEnd>\d+))?)?/;


// TESTS
// const testStrings = [
//     "創 1",
//     "創 2 3",
//     "马太福音 1 1 3",
//     "马太福音 1:1",
//     "马太福音 1:1-3",
//     "1_Samuels 1",
//     "2_John 2 3",
//     "2_John 2 3 4",
//     "Song_of_Songs 1 1 10",
//     "Genesis 1:1",
//     "1_Kings 1:1-10",
// ];

// testStrings.forEach((str) => {
//     console.log(str)
//     parseString(str)
// });


export const checkIsQueryComplete = (query:string): boolean => {
    return chinesePattern.test(query) || englishPattern.test(query)
   }

export const removeChapterAndVerse = (query:string): string => {
    const {book, chapter} = parseString(query)
    return (`${book} ${chapter}`)
}

export const incrementChapter = (query:string): string => {
    const {book, chapter} = parseString(query)
    return (`${book} ${chapter + 1}`)
}

export const decrementChapter = (query:string): string => {
    const {book, chapter} = parseString(query)
    if (chapter === 1) {
        return query
    }
    return (`${book} ${chapter - 1}`)
}

export const getChapter = (query:string): number => {
    const {chapter} = parseString(query)
    return chapter
}

export const toEnglishBookName = (book: string): string => {
    const bookObj = booksOfTheBible.find((b) => matchesBookName(b, book));
    return bookObj ? bookObj.english : book;
};

export const getMaxChapter = (book:string): number => {
    const bookObj = booksOfTheBible.find((bookObj) => matchesBookName(bookObj, book));
    if (!bookObj) {
      return 0; 
    }
    return bookObj.maxChapter;
}

export const checkIsAtMaxChapter = (query:string): boolean => {
    const {book, chapter} = parseString(query)
    return chapter >= getMaxChapter(book)
}

export const getActualLastVerse = (verseData: string, isESV: boolean): number | undefined => {
  if (isESV) {
    // ESV format: "[1] text [2] text..." — find the last [n]
    const matches = [...verseData.matchAll(/\[(\d+)\]/g)];
    return matches.length ? parseInt(matches[matches.length - 1][1]) : undefined;
  } else {
    // Chinese format: each line starts with "{verseNum} text" or "{verseNum1} {verseNum2} text"
    const lines = verseData.split('\n').map(l => l.trim()).filter(l => l.length > 0);
    const lastLine = lines[lines.length - 1];
    if (!lastLine) return undefined;
    const parts = lastLine.split(' ');
    if (parts.length >= 2 && /^\d+$/.test(parts[0]) && /^\d+$/.test(parts[1])) {
      return Math.max(parseInt(parts[0]), parseInt(parts[1]));
    }
    return /^\d+$/.test(parts[0]) ? parseInt(parts[0]) : undefined;
  }
};

export const getKeywords = (query:string): string[] => {
    return query.replaceAll("，", ",")
    .split(",")
    .map((keyword) => keyword.trim());
}